/**
 * หน้ารายละเอียดรายการ
 *
 * รูปโหลดผ่าน signed URL อายุสั้นที่ขอตอนเปิดหน้า ไม่ฝัง URL ถาวรไว้ใน HTML
 * ถ้า URL หมดอายุระหว่างเปิดหน้าค้างไว้ ผู้ใช้กดโหลดรูปใหม่ได้
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, type SessionResponse } from '../lib/api';
import type { CaptureView } from '../../shared/types';
import { formatDateTimeTh } from '../../shared/time';
import { coordinate, metres, statusLabel, statusPillClass } from '../lib/format';
import { canVoidCapture, minutesLeftToVoid } from '../../shared/permissions';
import { ConfirmDialog } from '../components/ConfirmDialog';

/** เหตุผลการลบต้องยาวอย่างน้อยเท่านี้ ตรงกับที่ฝั่ง server ตรวจ (voidCaptureSchema) */
const MIN_REASON_LENGTH = 5;

interface AuditEntry {
  id: string;
  action: string;
  createdAt: string;
  actorUserId: string | null;
}

const ACTION_LABELS_TH: Record<string, string> = {
  'capture.draft_created': 'สร้างรายการ',
  'capture.completed': 'บันทึกสำเร็จ',
  'capture.updated': 'แก้ไขข้อมูล',
  'capture.voided': 'ยกเลิกรายการ',
  'capture.duplicate_allowed': 'อนุญาตให้บันทึกซ้ำ',
  'capture.gps_override_requested': 'ขออนุมัติพิกัด',
  'capture.gps_override_approved': 'อนุมัติพิกัด',
  'capture.gps_override_rejected': 'ปฏิเสธคำขออนุมัติ',
  'capture.draft_expired': 'ลบฉบับร่างที่ค้าง',
};

export function DetailRoute({ session }: { session: SessionResponse }) {
  const { id } = useParams<{ id: string }>();
  const [capture, setCapture] = useState<CaptureView | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  const navigate = useNavigate();
  const isSupervisor = session.user.role !== 'field_officer';

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!id) return;
      try {
        const detail = await api.getCapture(id, signal);
        setCapture(detail.capture);

        if (detail.capture.hasImage) {
          const link = await api.imageUrl(id, signal);
          setImageUrl(link.url);
        }
        if (isSupervisor) {
          const history = await api.auditFor(id, signal);
          setAudit(history.items as AuditEntry[]);
        }
      } catch (caught) {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError(caught instanceof ApiError ? caught.message : 'โหลดข้อมูลไม่สำเร็จ');
      }
    },
    [id, isSupervisor],
  );

  useEffect(() => {
    const controller = new AbortController();
    /*
     * eslint-disable-next-line react-hooks/set-state-in-effect --
     * นี่คือการโหลดข้อมูลตอน mount ซึ่งจำเป็นต้องอยู่ใน effect
     * setState ทุกจุดใน load() เกิดหลัง await แล้วทั้งหมด ไม่ใช่ synchronous
     * แต่ rule ตรวจแบบ transitive จึงมองไม่เห็นความต่างนี้
     * มี AbortController ยกเลิก request เมื่อ component ถูก unmount แล้ว
     */
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const decide = async (decision: 'approved' | 'rejected') => {
    if (!id) return;
    setBusy(true);
    try {
      const result = await api.decideOverride(id, decision);
      setCapture(result.capture);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'ดำเนินการไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  /*
   * ลบรายการ
   *
   * ส่ง version ที่โหลดมาไปด้วย ถ้ามีคนแก้รายการนี้ระหว่างที่หน้าเปิดค้างอยู่
   * server จะตอบ VERSION_CONFLICT แทนที่จะลบทับของใหม่
   * กรณีนั้นโหลดข้อมูลใหม่ให้ผู้ใช้ดูก่อนตัดสินใจอีกครั้ง
   */
  const remove = async () => {
    if (!id || !capture) return;
    setBusy(true);
    setError(null);
    try {
      await api.voidCapture(id, deleteReason.trim(), capture.version);
      setConfirmingDelete(false);
      void navigate('/my', { replace: true });
    } catch (caught) {
      setConfirmingDelete(false);
      if (caught instanceof ApiError && caught.code === 'VERSION_CONFLICT') {
        setError('มีผู้อื่นแก้ไขรายการนี้ไปแล้ว โหลดข้อมูลใหม่แล้วตรวจอีกครั้งก่อนลบ');
        void load();
      } else {
        setError(caught instanceof ApiError ? caught.message : 'ลบรายการไม่สำเร็จ');
      }
    } finally {
      setBusy(false);
    }
  };

  if (error && !capture) {
    return (
      <div className="wrap stack">
        <div className="banner banner-bad" role="alert">
          <span>{error}</span>
        </div>
        <Link to="/my" className="btn btn-secondary btn-block">
          ย้อนกลับ
        </Link>
      </div>
    );
  }

  if (!capture) {
    return (
      <div className="wrap">
        <div className="empty">
          <span className="spinner" style={{ margin: '0 auto' }} />
        </div>
      </div>
    );
  }

  const canApprove = isSupervisor && capture.gpsOverride === 'requested';

  /*
   * ใช้กฎชุดเดียวกับที่ server บังคับ (shared/permissions.ts)
   * ถ้าซ่อนปุ่มอย่างเดียวโดยไม่มีกฎฝั่ง server จะไม่ใช่การควบคุมสิทธิ์
   * และถ้าเขียนกฎแยกกันสองที่ ปุ่มกับความจริงจะไม่ตรงกันสักวัน
   */
  const voidDecision = canVoidCapture(session.user, capture, {
    ownerEditWindowMinutes: session.settings.capture.ownerEditWindowMinutes,
  });
  const minutesLeft = minutesLeftToVoid(session.user, capture, {
    ownerEditWindowMinutes: session.settings.capture.ownerEditWindowMinutes,
  });
  const deletesPhoto = capture.hasImage;
  const reasonTooShort = deleteReason.trim().length < MIN_REASON_LENGTH;

  return (
    <div className="wrap stack-lg">
      <div className="spread">
        <h1>รายละเอียดรายการ</h1>
        <span className={statusPillClass(capture.status)}>{statusLabel(capture.status)}</span>
      </div>

      {error && (
        <div className="banner banner-bad" role="alert">
          <span>{error}</span>
        </div>
      )}

      <div className="card stack">
        <dl className="kv">
          <div>
            <dt>เลขทะเบียน</dt>
            <dd>{capture.customerRegistrationNormalized}</dd>
          </div>
          <div>
            <dt>รหัสรายการ</dt>
            <dd>{capture.id}</dd>
          </div>
          <div>
            <dt>ละติจูด</dt>
            <dd>{coordinate(capture.latitude)}</dd>
          </div>
          <div>
            <dt>ลองจิจูด</dt>
            <dd>{coordinate(capture.longitude)}</dd>
          </div>
          <div>
            <dt>ความแม่นยำ</dt>
            <dd>{metres(capture.accuracyM)}</dd>
          </div>
          <div>
            <dt>เกณฑ์ตอนบันทึก</dt>
            <dd>{metres(capture.gpsThresholdM)}</dd>
          </div>
          <div>
            <dt>เวลาบันทึก</dt>
            <dd>{formatDateTimeTh(capture.capturedAt)}</dd>
          </div>
          {capture.completedAt && (
            <div>
              <dt>เวลาที่สำเร็จ</dt>
              <dd>{formatDateTimeTh(capture.completedAt)}</dd>
            </div>
          )}
          <div>
            <dt>ผู้บันทึก</dt>
            <dd>{capture.createdByDisplayName ?? capture.createdBy}</dd>
          </div>
          {capture.areaCode && (
            <div>
              <dt>พื้นที่</dt>
              <dd>{capture.areaCode}</dd>
            </div>
          )}
        </dl>

        {/*
          ลิงก์ไปแอปแผนที่ของเครื่องแทนการฝังแผนที่ในหน้า
          เพราะการฝังต้องใช้ tile provider ภายนอก ซึ่งขัดกับ CSP ที่ตั้งไว้
          และเพิ่มค่าใช้จ่ายรายเดือน — ดู docs/open-questions.md
        */}
        <a
          className="btn btn-secondary btn-block"
          href={`geo:${capture.latitude},${capture.longitude}?q=${capture.latitude},${capture.longitude}`}
          rel="noopener"
        >
          เปิดพิกัดในแอปแผนที่
        </a>
      </div>

      {capture.gpsOverride !== 'none' && (
        <div
          className={capture.gpsOverride === 'approved' ? 'banner banner-ok' : 'banner banner-warn'}
        >
          <div className="grow">
            <b>
              {capture.gpsOverride === 'requested' && 'รออนุมัติพิกัดจากหัวหน้างาน'}
              {capture.gpsOverride === 'approved' && 'พิกัดได้รับการอนุมัติแล้ว'}
              {capture.gpsOverride === 'rejected' && 'คำขออนุมัติถูกปฏิเสธ'}
            </b>
            {capture.gpsOverrideReason}
            {capture.gpsApprovedAt && (
              <div className="small" style={{ marginTop: 4 }}>
                โดย {capture.gpsApprovedByDisplayName ?? capture.gpsApprovedBy} ·{' '}
                {formatDateTimeTh(capture.gpsApprovedAt)}
              </div>
            )}
          </div>
        </div>
      )}

      {capture.hasImage && (
        <div className="card stack">
          <h2>ภาพหน้างาน</h2>
          <div className="photo-frame">
            {imageUrl ? (
              <img src={imageUrl} alt={`ภาพของรายการ ${capture.customerRegistrationNormalized}`} />
            ) : (
              <span className="muted small">กำลังโหลดรูป…</span>
            )}
          </div>
          <div className="photo-meta">
            <span>{capture.imageContentType?.replace('image/', '').toUpperCase()}</span>
            <span>{Math.round((capture.imageSizeBytes ?? 0) / 1024)} KB</span>
            <span>{capture.imageSha256?.slice(0, 12)}…</span>
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-block"
            onClick={() => {
              setError(null);
              void load();
            }}
          >
            โหลดรูปใหม่ (ลิงก์หมดอายุใน 5 นาที)
          </button>
        </div>
      )}

      {isSupervisor && audit.length > 0 && (
        <div className="card stack">
          <h2>ประวัติการเปลี่ยนแปลง</h2>
          <div className="perf" />
          <ol className="stack" style={{ gap: 'var(--space-2)', listStyle: 'none', padding: 0 }}>
            {audit.map((entry) => (
              <li key={entry.id} className="spread small">
                <span>{ACTION_LABELS_TH[entry.action] ?? entry.action}</span>
                <span className="mono muted">{formatDateTimeTh(entry.createdAt)}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {voidDecision.allowed ? (
        <div className="card stack">
          <h2>ลบรายการนี้</h2>
          <p className="small muted">
            ข้อมูลจะถูกทำเครื่องหมายว่าลบแล้วและไม่แสดงในรายการอีก
            {deletesPhoto ? ' รูปถ่ายจะถูกลบออกจากที่จัดเก็บอย่างถาวร' : ''}{' '}
            ประวัติการลบยังถูกเก็บไว้ตรวจสอบย้อนหลังได้
          </p>
          {minutesLeft !== null && minutesLeft > 0 && (
            <p className="small muted">
              ลบเองได้อีก {minutesLeft} นาที หลังจากนั้นต้องแจ้งหัวหน้างาน
            </p>
          )}
          <button
            type="button"
            className="btn btn-danger btn-block"
            disabled={busy}
            onClick={() => {
              setDeleteReason('');
              setError(null);
              setConfirmingDelete(true);
            }}
          >
            ลบรายการนี้
          </button>
        </div>
      ) : (
        capture.status !== 'void' &&
        session.user.id === capture.createdBy && (
          <p className="small muted">{voidDecision.reason}</p>
        )
      )}

      <ConfirmDialog
        open={confirmingDelete}
        title="ยืนยันการลบรายการ"
        confirmLabel="ยืนยันลบ"
        busy={busy}
        confirmDisabled={reasonTooShort}
        onCancel={() => setConfirmingDelete(false)}
        onConfirm={() => void remove()}
      >
        <div className="banner banner-bad">
          <span>การลบย้อนกลับไม่ได้ กรุณาตรวจว่าเป็นรายการที่ต้องการจริง</span>
        </div>

        <dl className="kv">
          <div>
            <dt>เลขทะเบียน</dt>
            <dd>{capture.customerRegistrationNormalized}</dd>
          </div>
          <div>
            <dt>เวลาบันทึก</dt>
            <dd>{formatDateTimeTh(capture.capturedAt)}</dd>
          </div>
          <div>
            <dt>ผู้บันทึก</dt>
            <dd>{capture.createdByDisplayName ?? capture.createdBy}</dd>
          </div>
          <div>
            <dt>รูปถ่าย</dt>
            <dd>{deletesPhoto ? 'มี — จะถูกลบถาวร' : 'ไม่มี'}</dd>
          </div>
        </dl>

        <div className="field">
          <label className="label" htmlFor="delete-reason">
            เหตุผลที่ลบ
          </label>
          <input
            id="delete-reason"
            className="input"
            value={deleteReason}
            placeholder="เช่น บันทึกซ้ำ หรือ เลขทะเบียนผิด"
            onChange={(event) => setDeleteReason(event.target.value)}
            disabled={busy}
          />
          <span className="hint">
            เหตุผลถูกเก็บไว้ในประวัติการเปลี่ยนแปลง อย่างน้อย {MIN_REASON_LENGTH} ตัวอักษร
          </span>
        </div>
      </ConfirmDialog>

      {canApprove && (
        <div className="actionbar">
          <button
            type="button"
            className="btn btn-block"
            disabled={busy}
            onClick={() => void decide('approved')}
          >
            อนุมัติพิกัดนี้
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-block"
            disabled={busy}
            onClick={() => void decide('rejected')}
          >
            ปฏิเสธคำขอ
          </button>
        </div>
      )}
    </div>
  );
}
